import { useFormik } from "formik";
import InputField from "../../ui/form/InputField";
import { ComplaintFormValidation } from "../../../validation/formValidation";
import usePost from "../../../hooks/api/usePost";
import apiRequest from "../../../utils/apiRequest";
import FileInputField from "../../ui/form/FileInputField";
import Button from "../../ui/Button";
import React, { useState } from "react";
import axios from "axios";
import handleApiError from "./handleApiError";
import FullScreenLoader from "../../ui/FullScreenLoader";
import toast from "react-hot-toast";
import { DASHBOARD_ROUTE, HOME_ROUTE } from "../../../routes/web/rootRoutes";
import PaymentOptions from "./PaymentOptions";
import { useNavigate } from "react-router-dom";

const ComplaintForm = ({ data = {} }) => {
  const [errors, setErrors] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [submittedFormData, setSubmittedFormData] = useState(null);
       const navigate = useNavigate(); 

  const userString = localStorage.getItem('user');
  let union_code;
  if (userString) {
    const user = JSON.parse(userString);
    union_code = user.union_code;
  }

  const {
    post,
    isLoading: postLoading,
    error,
    serverErrors,
  } = usePost(apiRequest.createForm);

  const formik = useFormik({
    validationSchema: ComplaintFormValidation,

    initialValues: {
      // Member Details
      member_no: data?.member_no || union_code || "",
      date: data?.date || new Date().toISOString().split('T')[0],
      member_name: data?.member_name || "",
      designation: data?.designation || "",
      address: data?.address || "",
      mobile: data?.mobile || "",
      email: data?.email || "",

      // Production Details
      production_name: data?.production_name || "",
      production_office_address: data?.production_office_address || "",
      production_email: data?.production_email || "",

      // Producer Details
      producer_name: data?.producer_name || "",
      producer_mobile: data?.producer_mobile || "",
      producer_email: data?.producer_email || "",

      // PD/Art Director Details
      pd_name: data?.pd_name || "",
      pd_mobile: data?.pd_mobile || "",
      pd_email: data?.pd_email || "",

      // Manager Details
      manager_name: data?.manager_name || "",
      manager_mobile: data?.manager_mobile || "",
      manager_email: data?.manager_email || "",

      // Project & Payment Details
      project_name: data?.project_name || "",
      total_amount: data?.total_amount || "",
      amount_received: data?.amount_received || "",
      balance_amount: data?.balance_amount || "",

      // Additional Details
      remark: data?.remark || "",

      // File Uploads
      member_photo: null,
      member_signature: null,
    },

    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);

      try {
        const formData = new FormData();

        // Append all text fields
        const textFields = [
          "member_no",
          "date",
          "member_name",
          "designation",
          "address",
          "mobile",
          "email",
          "production_name",
          "production_office_address",
          "production_email",
          "producer_name",
          "producer_mobile",
          "producer_email",
          "pd_name",
          "pd_mobile",
          "pd_email",
          "manager_name",
          "manager_mobile",
          "manager_email",
          "project_name",
          "total_amount",
          "amount_received",
          "balance_amount",
          "remark",
        ];

        textFields.forEach((field) => {
          if (values[field]) formData.append(field, values[field]);
        });

        if (values.member_photo) formData.append("member_photo", values.member_photo);
        if (values.member_signature) formData.append("signature", values.member_signature);

        console.log("Submitting FormData:");
        for (let pair of formData.entries()) {
          console.log(pair[0], pair[1]);
        }

        const token = localStorage.getItem('useAccessToken');
        if (!token) {
          toast.error("Authentication token not found. Please login again.");
          setIsSubmitting(false);
          return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          toast.error("API URL not configured");
          setIsSubmitting(false);
          return;
        }

        // Make API call
        const response = await axios.post(
          `${apiUrl}complaint`,
          formData,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
            maxBodyLength: Infinity,
            timeout: 30000,
          }
        );

        console.log("✅ Complaint submitted successfully:", response.data);

        setSubmissionResponse(response.data);
        setSubmittedFormData(values);
        
        toast.success("Complaint submitted successfully!");
        resetForm();

        setTimeout(() => {
    navigate(DASHBOARD_ROUTE);
          
          setIsSubmitting(false);
          setShowPayment(true);

        }, 1500);

      } catch (error) {
        console.error("Submission error:", error);

        if (error.response) {
          console.error("Server Error:", error.response.data);
          toast.error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          console.error("No response from server");
          toast.error("No response from server. Please check your internet connection.");
        } else {
          console.error("Request error:", error.message);
          toast.error(`Error: ${error.message}`);
        }

        handleApiError(error, setErrors, "Failed to submit Complaint form");
        setIsSubmitting(false);
      }
    },
  });

  const setFieldValue = (field, value) => {
    formik.setFieldValue(field, value);
  };

  const handleClosePayment = () => {
    setShowPayment(false);
    window.location.href = HOME_ROUTE;
  };

  if (showPayment) {
    return (
      <>
        {showPayment && (
          <PaymentOptions
            onClose={handleClosePayment}
          />
        )}
      </>
    )
  }
  return (
    <>
      {isSubmitting && (
        <FullScreenLoader message="Submitting your complaint application..." />
      )}



      <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br min-h-screen">
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 to-violet-900 text-white p-6">
            <h1 className="text-center text-3xl font-bold mb-2">
              COMPLAINT FORM
            </h1>
          </div>

          <form onSubmit={formik.handleSubmit} className="p-8">
            {/* Section 1: Member Details */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border-l-4 border-violet-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">👤</span> Member Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Member No. <span className="text-red-500">*</span></label>
                  <InputField
                    id="member_no"
                    placeholder="Enter Membership No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_no}
                    error={formik.touched.member_no && formik.errors.member_no}
                    serverError={serverErrors?.member_no}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Date <span className="text-red-500">*</span></label>
                  <InputField
                    id="date"
                    type="date"
                    placeholder="Select Date"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.date}
                    error={formik.touched.date && formik.errors.date}
                    serverError={serverErrors?.date}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Member's Name <span className="text-red-500">*</span></label>
                  <InputField
                    id="member_name"
                    placeholder="Enter Member Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.member_name}
                    error={formik.touched.member_name && formik.errors.member_name}
                    serverError={serverErrors?.member_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Designation <span className="text-red-500">*</span></label>
                  <InputField
                    id="designation"
                    placeholder="Enter designation"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.designation}
                    error={formik.touched.designation && formik.errors.designation}
                    serverError={serverErrors?.designation}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">Address <span className="text-red-500">*</span></label>
                  <InputField
                    id="address"
                    placeholder="Enter Complete Address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.address}
                    error={formik.touched.address && formik.errors.address}
                    serverError={serverErrors?.address}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Mobile No. <span className="text-red-500">*</span></label>
                  <InputField
                    id="mobile"
                    placeholder="Enter mobile number"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.mobile}
                    error={formik.touched.mobile && formik.errors.mobile}
                    serverError={serverErrors?.mobile}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Email ID <span className="text-red-500">*</span></label>
                  <InputField
                    id="email"
                    type="email"
                    placeholder="Enter email address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.email}
                    error={formik.touched.email && formik.errors.email}
                    serverError={serverErrors?.email}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Production Details */}
            <div className="mb-8 p-6 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">🎬</span> Production Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">Complaint against Production Name <span className="text-red-500">*</span></label>
                  <InputField
                    id="production_name"
                    placeholder="Enter Complaint against Production Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.production_name}
                    error={formik.touched.production_name && formik.errors.production_name}
                    serverError={serverErrors?.production_name}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">Production Office Address</label>
                  <InputField
                    id="production_office_address"
                    placeholder="Enter Production Office Address"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.production_office_address}
                    error={formik.touched.production_office_address && formik.errors.production_office_address}
                    serverError={serverErrors?.production_office_address}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Production Email</label>
                  <InputField
                    id="production_email"
                    type="email"
                    placeholder="Enter Production Email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.production_email}
                    error={formik.touched.production_email && formik.errors.production_email}
                    serverError={serverErrors?.production_email}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Producer Details */}
            <div className="mb-8 p-6 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">🎥</span> Producer Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Name of the Producer</label>
                  <InputField
                    id="producer_name"
                    placeholder="Enter Producer Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.producer_name}
                    error={formik.touched.producer_name && formik.errors.producer_name}
                    serverError={serverErrors?.producer_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Producer Contact No</label>
                  <InputField
                    id="producer_mobile"
                    placeholder="Enter Producer Contact No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.producer_mobile}
                    error={formik.touched.producer_mobile && formik.errors.producer_mobile}
                    serverError={serverErrors?.producer_mobile}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Producer Email</label>
                  <InputField
                    id="producer_email"
                    type="email"
                    placeholder="Enter Producer Email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.producer_email}
                    error={formik.touched.producer_email && formik.errors.producer_email}
                    serverError={serverErrors?.producer_email}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: PD/Art Director Details */}
            <div className="mb-8 p-6 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">🎨</span> PD/Art Director Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Name of the PD/Art Director</label>
                  <InputField
                    id="pd_name"
                    placeholder="Enter PD/Art Director Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.pd_name}
                    error={formik.touched.pd_name && formik.errors.pd_name}
                    serverError={serverErrors?.pd_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">PD/Art Director Contact No</label>
                  <InputField
                    id="pd_mobile"
                    placeholder="Enter PD/Art Director Contact No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.pd_mobile}
                    error={formik.touched.pd_mobile && formik.errors.pd_mobile}
                    serverError={serverErrors?.pd_mobile}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">PD/Art Director Email</label>
                  <InputField
                    id="pd_email"
                    type="email"
                    placeholder="Enter PD/Art Director Email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.pd_email}
                    error={formik.touched.pd_email && formik.errors.pd_email}
                    serverError={serverErrors?.pd_email}
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Production Manager Details */}
            <div className="mb-8 p-6 bg-purple-50 rounded-lg border-l-4 border-purple-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📋</span> Production Manager Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Name of the LP/EP/Production Manager</label>
                  <InputField
                    id="manager_name"
                    placeholder="Enter Manager Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.manager_name}
                    error={formik.touched.manager_name && formik.errors.manager_name}
                    serverError={serverErrors?.manager_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Production Manager Contact No</label>
                  <InputField
                    id="manager_mobile"
                    placeholder="Enter Contact No"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.manager_mobile}
                    error={formik.touched.manager_mobile && formik.errors.manager_mobile}
                    serverError={serverErrors?.manager_mobile}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Production Manager Email</label>
                  <InputField
                    id="manager_email"
                    type="email"
                    placeholder="Enter Email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.manager_email}
                    error={formik.touched.manager_email && formik.errors.manager_email}
                    serverError={serverErrors?.manager_email}
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Project & Payment Details */}
            <div className="mb-8 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">💰</span> Project & Payment Details
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">Name: Film / Serial / Ad / Video Album / Web Series <span className="text-red-500">*</span></label>
                  <InputField
                    id="project_name"
                    placeholder="Enter Project Name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.project_name}
                    error={formik.touched.project_name && formik.errors.project_name}
                    serverError={serverErrors?.project_name}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Total Amount <span className="text-red-500">*</span></label>
                  <InputField
                    id="total_amount"
                    type="number"
                    placeholder="Enter Total Amount"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.total_amount}
                    error={formik.touched.total_amount && formik.errors.total_amount}
                    serverError={serverErrors?.total_amount}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Amount Received</label>
                  <InputField
                    id="amount_received"
                    type="number"
                    placeholder="Enter Amount Received"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.amount_received}
                    error={formik.touched.amount_received && formik.errors.amount_received}
                    serverError={serverErrors?.amount_received}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Total Balance Amount <span className="text-red-500">*</span></label>
                  <InputField
                    id="balance_amount"
                    type="number"
                    placeholder="Enter Balance Amount"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.balance_amount}
                    error={formik.touched.balance_amount && formik.errors.balance_amount}
                    serverError={serverErrors?.balance_amount}
                  />
                </div>

                <div className="space-y-2 lg:col-span-2">
                  <label className="block font-semibold text-gray-700">Remark</label>
                  <InputField
                    id="remark"
                    placeholder="Enter Remark"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.remark}
                    error={formik.touched.remark && formik.errors.remark}
                    serverError={serverErrors?.remark}
                  />
                </div>
              </div>
            </div>

            {/* Section 7: Documents Upload */}
            <div className="mb-8 p-6 bg-pink-50 rounded-lg border-l-4 border-pink-500">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📎</span> Documents Upload <span className="text-sm font-normal text-gray-600 ml-2">(Please upload required documents)</span>
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Member Photo <span className="text-red-500">*</span></label>
                  <FileInputField
                    id="member_photo"
                    onChange={(event) => {
                      setFieldValue("member_photo", event.currentTarget.files[0]);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.member_photo && formik.errors.member_photo}
                    serverError={serverErrors?.member_photo}
                  />
                  <p className="text-xs text-gray-500">Upload passport size photo (JPG, PNG)</p>
                </div>

                <div className="space-y-2">
                  <label className="block font-semibold text-gray-700">Member Signature <span className="text-red-500">*</span></label>
                  <FileInputField
                    id="member_signature"
                    onChange={(event) => {
                      setFieldValue("member_signature", event.currentTarget.files[0]);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.member_signature && formik.errors.member_signature}
                    serverError={serverErrors?.member_signature}
                  />
                  <p className="text-xs text-gray-500">Upload scanned signature (JPG, PNG)</p>
                </div>
              </div>
            </div>

            {/* Section 8: Rules & Regulations */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border-2 border-gray-200">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="mr-2">📜</span> RULES & REGULATIONS
              </h2>

              <div className="text-sm space-y-3">
                <ol className="list-decimal pl-5 space-y-2">
                  <li>DISPUTE cases of members will be fought in the association only for the work as per their designation strictly.</li>
                  <li>Association is strictly against any contract system. Any disputes received pertaining to such contracts shall not be accepted.</li>
                  <li>Dispute cases shall be accepted only for member's professional fees. No dispute shall be accepted for any property, transport or labour charges. Disputes of members pertaining to their work in setting and shooting shall only be accepted. Disputes for any work done in the personal capacity of the member outside the film industry shall not be accepted.</li>
                  <li>Members who have received the membership number are only allowed to submit dispute matters in the association. Any member whose subscription installments are pending shall not be allowed to submit dispute matters.</li>
                  <li>Any subscription pending should be first cleared before submitting any dispute matter.</li>
                  <li>Complaints of members with inappropriate details such as the email Id's, Phone Numbers, production house address and other relevant details with proof of outstanding dues shall not be accepted.</li>
                  <li>Member-Member working is compulsory. Member - Nonmember complaints shall not be accepted.</li>
                  <li>We shall not handle any disputes in which either parties have approached the court of law or police station for resolution of their disputes. Matter which are sub-judice shall not be accepted by the association.</li>
                  <li>Any disputes which does not fit in the above criteria can be discussed in the committee meeting and considered accordingly.</li>
                  <li>ACTADCD shall charge 10% of the Dispute settlement amount towards the welfare fund of the association.</li>
                </ol>
              </div>

              {/* Agreement Checkbox */}
              <div className="mt-6 flex items-center">
                <input
                  type="checkbox"
                  id="agree"
                  name="agree"
                  onChange={(e) => formik.setFieldValue('agree', e.target.checked)}
                  className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                />
                <label htmlFor="agree" className="ml-2 text-sm text-gray-700">
                  I have read and agree to all the rules and regulations mentioned above <span className="text-red-500">*</span>
                </label>
              </div>
              {formik.touched.agree && formik.errors.agree && (
                <p className="mt-1 text-xs text-red-500">{formik.errors.agree}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex justify-end items-center bg-gray-50 p-6 rounded-lg">
              <Button
                variant="primary"
                type="submit"
                isLoading={postLoading || isSubmitting}
                disabled={postLoading || isSubmitting}
                className="px-8 py-3 text-lg"
              >
                {isSubmitting ? 'Submitting...' : '📤 Submit Complaint'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ComplaintForm;